import { useMutation } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type SyntheticEvent } from 'react';

import { Button } from '../components/Button';
import { Field, inputClass } from '../components/Field';
import { PageHeader } from '../components/PageHeader';
import { useToast } from '../components/Toast';
import { useAuth } from '../features/auth/AuthProvider';
import type { SessionTenant } from '../features/auth/session';
import { ApiError } from '../lib/api';
import { formatClock, timeZones } from '../lib/time';

export function SettingsPage() {
  const { session, request, setTenant } = useAuth();
  const { notify } = useToast();
  const tenant = session?.tenant;
  const [name, setName] = useState(tenant?.name ?? '');
  const [zone, setZone] = useState(tenant?.timezone ?? 'UTC');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const zones = useMemo(() => timeZones(), []);

  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => {
      setNow(new Date());
    }, 1000);
    return () => {
      window.clearInterval(id);
    };
  }, []);

  const mutation = useMutation({
    mutationFn: (body: { name?: string; timezone?: string }) =>
      request<SessionTenant>('PATCH', '/api/v1/tenant', body),
    onSuccess: (t) => {
      setTenant(t);
      setName(t.name);
      setZone(t.timezone);
      notify('Configurações da organização salvas.');
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 422) {
        const f = err.fields;
        setErrors({
          ...(f.name ? { name: 'Informe um nome com até 200 caracteres.' } : {}),
          ...(f.timezone ? { timezone: 'Fuso horário inválido.' } : {}),
        });
      } else if (err instanceof ApiError && err.status === 403) {
        setFormError('Você não tem permissão para alterar a organização.');
      } else {
        setFormError('Não foi possível salvar. Tente novamente.');
      }
    },
  });

  if (!tenant) return null;
  const dirty = name.trim() !== tenant.name || zone !== tenant.timezone;

  const submit = (e: SyntheticEvent) => {
    e.preventDefault();
    setErrors({});
    setFormError(null);
    const body: { name?: string; timezone?: string } = {};
    if (name.trim() !== tenant.name) body.name = name;
    if (zone !== tenant.timezone) body.timezone = zone;
    mutation.mutate(body);
  };

  return (
    <>
      <PageHeader
        title="Organização"
        description="Identificação e preferências regionais da organização."
      />
      <div className="p-4 sm:p-6">
        <form
          onSubmit={submit}
          noValidate
          className="max-w-xl space-y-5 rounded-lg border border-line bg-surface p-5"
        >
          {formError && (
            <p role="alert" className="text-[13px] text-crit">
              {formError}
            </p>
          )}
          <Field label="Nome da organização" error={errors.name}>
            {({ id, describedBy, invalid }) => (
              <input
                id={id}
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                }}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                className={inputClass}
              />
            )}
          </Field>
          <Field
            label="Identificador"
            hint="Usado no login. Só o Super administrador altera, na tela Empresas da plataforma."
          >
            {({ id, describedBy }) => (
              <input
                id={id}
                value={tenant.slug}
                readOnly
                aria-describedby={describedBy}
                className={`${inputClass} font-mono text-fg-muted`}
              />
            )}
          </Field>
          <Field
            label="Fuso horário"
            hint={`Todos os horários da plataforma são exibidos neste fuso. Agora: ${formatClock(now, zone)}`}
            error={errors.timezone}
          >
            {({ id, describedBy, invalid }) => (
              <select
                id={id}
                value={zone}
                onChange={(e) => {
                  setZone(e.target.value);
                }}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                className={inputClass}
              >
                {zones.map((z) => (
                  <option key={z} value={z}>
                    {z.replaceAll('_', ' ')}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <div className="flex justify-end gap-2 border-t border-line pt-4">
            <Button
              disabled={!dirty || mutation.isPending}
              onClick={() => {
                setName(tenant.name);
                setZone(tenant.timezone);
                setErrors({});
              }}
            >
              Descartar
            </Button>
            <Button variant="primary" type="submit" disabled={!dirty || mutation.isPending}>
              {mutation.isPending ? 'Salvando…' : 'Salvar'}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
}
